import { useState, useEffect } from 'react'
import { Search, Plus, FileText, Loader2, MoreHorizontal, Eye, Edit, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Layout } from '@/components/layout'
import { usePracticeCases, useDeletePracticeCase } from '@/api/practice-cases'
import { Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { useToast } from '@/lib/hooks/useToast'
import { CreatePracticeCaseDialog } from './components/CreatePracticeCaseDialog'
import { EditPracticeCaseDialog } from './components/EditPracticeCaseDialog'
import { DeletePracticeCaseDialog } from './components/DeletePracticeCaseDialog'
import { ViewPracticeCaseDialog } from './components/ViewPracticeCaseDialog'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { PracticeCase } from '@/api/practice-cases'

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'ready':
      return (
        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          Klaar
        </Badge>
      )
    case 'generating':
      return (
        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Genereren...
        </Badge>
      )
    case 'queued':
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
          <Clock className="h-3 w-3 mr-1" />
          In wachtrij
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Fout
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

export function AdminPracticeCasesPage() {
  const { toast } = useToast()
  const [filters, setFilters] = useState({
    search: '',
    status: 'all' as string,
  })
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedCase, setSelectedCase] = useState<PracticeCase | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // Permission checks
  const canCreatePractice = useHasPermission('practice.create')
  const canUpdatePractice = useHasPermission('practice.update')
  const canDeletePractice = useHasPermission('practice.delete')

  const { data: practiceCasesData, isLoading, refetch } = usePracticeCases(false)
  const { mutateAsync: deleteCase, isPending: isDeleting } = useDeletePracticeCase()

  // Poll for updates when cases are queued or processing
  useEffect(() => {
    const hasActiveCases = practiceCasesData?.data?.some(
      practiceCase => practiceCase.status === 'queued' || practiceCase.status === 'generating'
    )

    if (hasActiveCases) {
      const interval = setInterval(() => {
        refetch()
      }, 3000) // Poll every 3 seconds

      return () => clearInterval(interval)
    }

    return undefined
  }, [practiceCasesData?.data, refetch])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null)
    }

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }

    return undefined
  }, [openMenuId])

  const filteredPracticeCases =
    practiceCasesData?.data
      ?.filter(practiceCase => {
        // Search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase()
          const matchesSearch =
            practiceCase.title.toLowerCase().includes(searchLower) ||
            practiceCase.location.toLowerCase().includes(searchLower)
          if (!matchesSearch) return false
        }

        // Status filter
        if (filters.status && filters.status !== 'all') {
          if (practiceCase.status !== filters.status) {
            return false
          }
        } else if (filters.status === 'all') {
          // When showing all statuses, exclude deleted cases by default
          if (practiceCase.status === 'deleted') {
            return false
          }
        }

        return true
      })
      .sort(
        (a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
      ) || []

  const handleView = (practiceCase: PracticeCase) => {
    setSelectedCase(practiceCase)
    setIsViewDialogOpen(true)
  }

  const handleEdit = (practiceCase: PracticeCase) => {
    setSelectedCase(practiceCase)
    setIsEditDialogOpen(true)
  }

  const handleDelete = (practiceCase: PracticeCase) => {
    setSelectedCase(practiceCase)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedCase) return

    try {
      await deleteCase(selectedCase.id)
      toast({
        title: 'Oefenscenario verwijderd',
        description: `Oefenscenario "${selectedCase.title}" is verwijderd.`,
        variant: 'success',
      })
      setIsDeleteDialogOpen(false)
      setSelectedCase(null)
    } catch (error) {
      console.error('Failed to delete practice case:', error)
      toast({
        title: 'Fout',
        description: 'Kon oefenscenario niet verwijderen.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Layout>
      {/* Hero Section */}
      <div className="container mx-auto px-4 md:px-6 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2 flex items-center gap-3">
              <FileText className="h-8 w-8" />
              Oefenscenario's
            </h1>
            <p className="text-base md:text-xl text-muted-foreground">
              Beheer oefenscenario's voor trainingsoefeningen
            </p>
          </div>
          {canCreatePractice && (
            <Button
              variant="gradient"
              size="lg"
              className="gap-2 w-full md:w-auto"
              onClick={() => setIsCreateDialogOpen(true)}
            >
              <Plus className="w-5 h-5" />
              <span className="md:inline">Nieuw oefenscenario</span>
            </Button>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 md:px-6 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-8">
          {/* Left Column - Filters */}
          <div className="lg:col-span-1 space-y-4 md:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Filters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <fieldset>
                  <legend className="sr-only">Filters</legend>

                  {/* Search */}
                  <div className="relative mb-4">
                    <Label htmlFor="practice-case-search" className="sr-only">
                      Zoeken
                    </Label>
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <Input
                      id="practice-case-search"
                      placeholder="Zoeken..."
                      value={filters.search}
                      onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                      aria-label="Zoeken in oefenscenario's"
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <Label
                      htmlFor="practice-case-status"
                      className="block text-sm font-medium mb-2"
                    >
                      Status
                    </Label>
                    <Select
                      value={filters.status}
                      onValueChange={(value: string) =>
                        setFilters(prev => ({ ...prev, status: value }))
                      }
                    >
                      <SelectTrigger
                        className="w-full"
                        id="practice-case-status"
                        aria-label="Filter op status"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle statussen</SelectItem>
                        <SelectItem value="ready">Klaar</SelectItem>
                        <SelectItem value="generating">Genereren...</SelectItem>
                        <SelectItem value="queued">In wachtrij</SelectItem>
                        <SelectItem value="error">Fout</SelectItem>
                        <SelectItem value="deleted">Verwijderd</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Verwijderde oefenscenario's worden standaard uitgefilterd
                    </p>
                  </div>
                </fieldset>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - List */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <Card className="text-center py-12" role="status" aria-live="polite">
                <CardContent>
                  <Loader2
                    className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-spin"
                    aria-hidden="true"
                  />
                  <h3 className="text-xl font-semibold mb-2">Oefenscenario's laden...</h3>
                  <p className="text-muted-foreground">
                    Even geduld terwijl we de oefenscenario's ophalen.
                  </p>
                </CardContent>
              </Card>
            ) : filteredPracticeCases.length === 0 ? (
              /* Empty State */
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Geen oefenscenario's gevonden</h3>
                  <p className="text-muted-foreground mb-6">
                    {filters.search || filters.status !== 'all'
                      ? "Geen oefenscenario's gevonden met de huidige filters."
                      : "Er zijn nog geen oefenscenario's aangemaakt."}
                  </p>
                  {canCreatePractice && (
                    <Button
                      variant="gradient"
                      className="gap-2"
                      onClick={() => setIsCreateDialogOpen(true)}
                    >
                      <Plus className="w-4 h-4" />
                      Eerste oefenscenario maken
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Practice Cases List */
              <div className="space-y-4">
                {filteredPracticeCases.map(practiceCase => (
                  <Card key={practiceCase.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 md:p-6">
                      {/* Header row: Title left, Actions right */}
                      <div className="flex items-start justify-between gap-4 mb-3 md:mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm md:text-lg font-semibold text-foreground truncate">
                            {practiceCase.title}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">
                            {practiceCase.location}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusBadge(practiceCase.status)}
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={e => {
                                e.stopPropagation()
                                setOpenMenuId(
                                  openMenuId === practiceCase.id ? null : practiceCase.id
                                )
                              }}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Acties openen</span>
                            </Button>
                            {openMenuId === practiceCase.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-border rounded-md shadow-lg p-1 z-50">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start gap-2"
                                  onClick={() => {
                                    handleView(practiceCase)
                                    setOpenMenuId(null)
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                  Details bekijken
                                </Button>
                                {practiceCase.status !== 'deleted' && (
                                  <>
                                    {canUpdatePractice && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start gap-2"
                                        onClick={() => {
                                          handleEdit(practiceCase)
                                          setOpenMenuId(null)
                                        }}
                                        disabled={practiceCase.status === 'generating'}
                                      >
                                        <Edit className="h-4 w-4" />
                                        Bewerken
                                      </Button>
                                    )}
                                    {canDeletePractice && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="w-full justify-start gap-2 text-red-600 hover:text-red-600 hover:bg-red-50"
                                        onClick={() => {
                                          handleDelete(practiceCase)
                                          setOpenMenuId(null)
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Verwijderen
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Content row: Stats left, Metadata right */}
                      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 md:gap-0">
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-4 items-center mb-2">
                            <div className="text-xs md:text-sm">
                              <span className="font-medium text-muted-foreground">Artikelen:</span>{' '}
                              <span>{practiceCase.articleCount}</span>
                            </div>
                            <div className="text-xs md:text-sm">
                              <span className="font-medium text-muted-foreground">Bestand:</span>{' '}
                              <span>
                                {practiceCase.fileMetadata?.[0]?.originalFilename || 'Geen bestand'}
                              </span>
                            </div>
                          </div>
                          <div className="text-xs md:text-sm text-muted-foreground">
                            <span className="font-medium">Aangemaakt:</span>{' '}
                            {practiceCase.createdAt
                              ? new Date(practiceCase.createdAt).toLocaleString('nl-NL', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '-'}
                          </div>
                        </div>
                        <div className="flex-shrink-0 md:ml-4">
                          {practiceCase.createdBy && (
                            <span className="text-xs md:text-sm text-muted-foreground">
                              Door: {practiceCase.createdBy.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <CreatePracticeCaseDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      {selectedCase && (
        <>
          <ViewPracticeCaseDialog
            open={isViewDialogOpen}
            onOpenChange={setIsViewDialogOpen}
            practiceCase={selectedCase}
          />
          <EditPracticeCaseDialog
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            practiceCase={selectedCase}
          />
          <DeletePracticeCaseDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            practiceCase={selectedCase}
            onConfirm={confirmDelete}
            isDeleting={isDeleting}
          />
        </>
      )}
    </Layout>
  )
}
